import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Clock,
  DollarSign,
  Shield,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  FileText,
  Lock,
  Eye,
  Building2,
  Calculator,
  ChevronDown,
  ChevronUp,
  UserCheck,
  Settings,
  Database,
  Wallet,
  ClipboardList,
  AlertTriangle,
  ShoppingCart,
  Users,
  PackageSearch,
  Bot,
} from 'lucide-react';
import { useBranding } from '@/components/BrandingProvider';

// ────────────────────────────────────────────────────────────────────────────
// Paleta SoHo cálida (no usamos tokens del sidebar verde para no acoplar):
//   linen     #f5f1ea     (página)
//   sand      #ebe4d8     (sección alterna)
//   cream     #faf7f1     (cards suaves)
//   charcoal  #1f1d1a     (titulares)
//   stone     #5b554c     (texto)
//   terracota #b15a3c     (acento principal)
//   mustard   #c9943a     (acento secundario / underline)
//   olive     #7a8b5e     (success suave)
// ────────────────────────────────────────────────────────────────────────────

const MODULES = [
  {
    Icon: Clock,
    title: 'Asistencia GPS',
    desc: 'Entrada y salida con geolocalización y validación de radio.',
  },
  {
    Icon: Calculator,
    title: 'Nómina',
    desc: 'Semanal, aguinaldos y finiquitos LFT 76, 80 y 87.',
  },
  {
    Icon: Building2,
    title: 'Obras',
    desc: 'Multi-proyecto con presupuesto, facturación y utilidad.',
  },
  {
    Icon: DollarSign,
    title: 'Control Financiero',
    desc: 'KPIs por obra: comprometido, pagado, costo real y utilidad.',
  },
  {
    Icon: ShoppingCart,
    title: 'Compras',
    desc: 'Registro de facturas en PDF o foto con dedup de proveedores.',
  },
  {
    Icon: PackageSearch,
    title: 'Inventario',
    desc: 'Stock por almacén, kardex, traspasos y consumos a obra.',
  },
  {
    Icon: Wallet,
    title: 'Tesorería',
    desc: 'Cajas, cuentas bancarias y conciliación de movimientos.',
  },
  {
    Icon: Users,
    title: 'Empleados',
    desc: 'Alta segura con Supabase Admin, roles y asignaciones.',
  },
  {
    Icon: ClipboardList,
    title: 'Tiempo Extra',
    desc: 'Solicitud, aprobación y liquidación de horas extra.',
  },
  {
    Icon: AlertTriangle,
    title: 'Incidencias',
    desc: 'Registro y seguimiento de eventos en sitio.',
  },
  {
    Icon: BarChart3,
    title: 'Reportes',
    desc: 'Exportación a Excel con timestamps y trazabilidad.',
  },
  {
    Icon: Shield,
    title: 'Seguridad',
    desc: 'RBAC, RLS y bitácora de auditoría en tiempo real.',
  },
  {
    Icon: Bot,
    title: 'Asistente Alex AI',
    desc: 'Consulta y registra operaciones por chat con IA.',
  },
  {
    Icon: FileText,
    title: 'Catálogos',
    desc: 'Proveedores, clientes, almacenes y empresas.',
  },
];

const FEATURES = [
  {
    eyebrow: 'AUTOMATIZACIÓN',
    title: 'Reduce procesos manuales',
    desc: 'Sustituye hojas de cálculo y libretas por un back office único que cubre asistencia, nómina, finanzas, compras e inventario. Tu equipo deja de duplicar capturas.',
    bullets: [
      'Cierre automático de asistencia con pg_cron',
      'Nómina LFT con motor de cálculo propio',
      'Compras con factura adjunta y validación previa',
    ],
    mockTitle: 'Resumen operativo',
    mockRows: [
      { k: 'Asistencia hoy', v: '94%', pill: 'OK' },
      { k: 'Compras pendientes', v: '7', pill: 'REV' },
      { k: 'Nómina lista', v: '$485,210', pill: 'PAGAR' },
    ],
  },
  {
    eyebrow: 'VISIBILIDAD',
    title: 'Cuadro de mando diario',
    desc: 'Paneles, KPIs y tarjetas en tiempo real. Sabes cuánto se comprometió, cuánto se pagó, qué obra va arriba o debajo de presupuesto y dónde está el costo real.',
    bullets: [
      'Dashboards por obra y consolidados',
      'Vista segura de finanzas con RLS',
      'Costo aplicado vs gasto financiero',
    ],
    mockTitle: 'Control financiero',
    mockRows: [
      { k: 'Presupuesto', v: '$83,657,364', pill: 'BASE' },
      { k: 'Facturado', v: '$33,466,357', pill: '+12%' },
      { k: 'Por cobrar', v: '$24,647,217', pill: 'WATCH' },
    ],
  },
  {
    eyebrow: 'TRANSPARENCIA',
    title: 'Trazabilidad completa',
    desc: 'Cada acción queda registrada con usuario, rol, IP, módulo y descripción. Exporta a Excel para auditorías externas y deja una huella defendible de cada movimiento.',
    bullets: [
      'Bitácora logs_actividad con timestamps',
      'URLs firmadas con expiración para documentos',
      'Folios serializados sin colisiones',
    ],
    mockTitle: 'Bitácora reciente',
    mockRows: [
      { k: 'compra_registrar', v: 'OFICINA', pill: 'AUDIT' },
      { k: 'proveedor_crear', v: 'ADMIN', pill: 'AUDIT' },
      { k: 'pago_emitido', v: 'FINANZAS', pill: 'AUDIT' },
    ],
  },
];

const FAQ_DATA = [
  {
    question: '¿Cómo garantizan la seguridad de mis datos financieros?',
    answer:
      'Usamos Row Level Security (RLS) en Supabase PostgreSQL. Los datos financieros están protegidos por user_has_financial_access_v2(), que se evalúa en cada consulta. Cada usuario solo accede a las obras que tiene asignadas.',
  },
  {
    question: '¿Qué roles existen y qué permisos tiene cada uno?',
    answer:
      'Cuatro niveles: SuperAdmin (acceso total y configuración), Admin (empleados, nómina, compras y finanzas), Supervisor (sus obras asignadas, sin datos financieros) y Usuario (su propio registro de asistencia y perfil).',
  },
  {
    question: '¿Puedo auditar quién accedió a qué información?',
    answer:
      'Sí. La tabla logs_actividad registra cada acción con usuario, rol, módulo, descripción, IP y user-agent. El módulo de Monitoreo de Seguridad expone esta bitácora en tiempo real.',
  },
  {
    question: '¿Cómo se calcula la nómina?',
    answer:
      'El motor LFT cubre nómina semanal, aguinaldos (Art. 87), finiquitos (Art. 76 y 80) y vacaciones reforma 2023. Pago y reverso son atómicos vía RPC con advisory locks; los folios son seriales por año con ON CONFLICT.',
  },
  {
    question: '¿Cómo funciona el Asistente Alex AI?',
    answer:
      'Alex es un asistente conversacional con 21 herramientas sobre tu ERP: consulta obras, compras, proveedores, gastos, tesorería, inventario y RH; y puede registrar compras, crear proveedores e items con dedup difuso. Pagos, cancelaciones y movimientos manuales aún no están delegados.',
  },
  {
    question: '¿Funciona sin conexión?',
    answer:
      'Sí. Es una PWA con IndexedDB (Dexie), Service Worker y Background Sync. Las marcaciones de asistencia y registros se guardan localmente y se sincronizan al recuperar conexión.',
  },
  {
    question: '¿Puedo exportar reportes para auditorías externas?',
    answer:
      'Sí. El sistema genera Excel con datos completos de asistencia, nómina y finanzas, incluyendo timestamps, coordenadas GPS y trazabilidad línea por línea.',
  },
];

const ROLES = [
  {
    Icon: Settings,
    title: 'SuperAdmin',
    desc: 'Configuración del sistema y gestión de roles.',
    badge: 'Acceso total',
  },
  {
    Icon: Lock,
    title: 'Admin',
    desc: 'Empleados, nómina, compras y finanzas.',
    badge: 'Gestión operativa',
  },
  {
    Icon: Eye,
    title: 'Supervisor',
    desc: 'Obras asignadas, asistencia e incidencias.',
    badge: 'Sin datos financieros',
  },
  {
    Icon: UserCheck,
    title: 'Usuario',
    desc: 'Perfil personal y registro de asistencia.',
    badge: 'Solo su información',
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Componentes auxiliares
// ────────────────────────────────────────────────────────────────────────────
function Eyebrow({ children, color = '#c9943a' }) {
  return (
    <div className="inline-flex flex-col items-start mb-5">
      <span
        className="text-[11px] font-semibold tracking-[0.18em] text-[#5b554c]"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {children}
      </span>
      <span className="block w-10 h-[2px] mt-2" style={{ background: color }} />
    </div>
  );
}

function Serif({ children, className = '' }) {
  return (
    <span
      className={className}
      style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontWeight: 600,
        letterSpacing: '-0.01em',
      }}
    >
      {children}
    </span>
  );
}

function MockCard({ title, rows }) {
  return (
    <div className="relative">
      {/* sombra cálida detrás */}
      <div
        className="absolute -inset-3 rounded-[18px] rotate-[1.5deg]"
        style={{
          background: 'linear-gradient(135deg, #ebe4d8 0%, #e6dccb 100%)',
        }}
      />
      <div className="relative bg-white rounded-[14px] border border-[#e7dfd1] shadow-[0_8px_24px_-12px_rgba(31,29,26,0.18)] p-5">
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-[#efe7d8]">
          <div
            className="text-[13px] font-semibold text-[#1f1d1a]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {title}
          </div>
          <span className="text-[10px] tracking-widest text-[#a89886]">
            VISTA
          </span>
        </div>
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg"
              style={{ background: '#faf7f1' }}
            >
              <span className="text-[12px] text-[#1f1d1a] truncate">{r.k}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[12px] font-mono tabular-nums text-[#1f1d1a]">
                  {r.v}
                </span>
                <span
                  className="text-[10px] font-semibold tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: '#f3e6d4', color: '#8a5a1a' }}
                >
                  {r.pill}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-[#e7dfd1]">
      <button
        onClick={onToggle}
        className="w-full py-5 text-left flex items-center justify-between gap-4 hover:text-[#b15a3c] transition-colors"
      >
        <span
          className="font-semibold text-[#1f1d1a] text-base sm:text-lg"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {question}
        </span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#a89886] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#a89886] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-6">
          <p className="text-[#5b554c] leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Página
// ────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const branding = useBranding();
  const [openFAQ, setOpenFAQ] = useState(0);

  const companyName = branding?.nombre_empresa || 'Nova ERP de Obras';
  const logoUrl = branding?.logo_url || '/nova-icon.png';
  const goLogin = () => navigate('/login');

  return (
    <div
      className="min-h-screen antialiased text-[#1f1d1a]"
      style={{
        background: '#f5f1ea',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <Helmet>
        <title>{companyName} | ERP integral para empresas constructoras</title>
        <meta
          name="description"
          content="Asistencia GPS, nómina LFT, finanzas por obra con costo real, compras con factura adjunta, inventario y tesorería. Seguridad enterprise con RBAC y RLS."
        />
        <link rel="canonical" href="https://gyidcorporativo.app" />
      </Helmet>

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav
        className="sticky top-0 z-50 border-b border-[#e7dfd1]"
        style={{
          background: 'rgba(245,241,234,0.85)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logoUrl}
              alt={companyName}
              className="w-9 h-9 object-contain"
            />
            <span className="font-semibold text-[#1f1d1a] hidden sm:block tracking-tight">
              {companyName}
            </span>
          </div>
          <button
            onClick={goLogin}
            className="px-5 py-2 bg-[#1f1d1a] hover:bg-[#2c2925] text-[#f5f1ea] rounded-lg font-medium text-sm transition-colors shadow-sm"
          >
            Ingresar
          </button>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 pb-20 text-center">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.15em] mb-8"
            style={{ background: '#ebe4d8', color: '#8a5a1a' }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: '#b15a3c' }}
            />
            ERP INTEGRAL · CONSTRUCCIÓN Y OBRA CIVIL
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-[64px] leading-[1.05] mb-6 text-[#1f1d1a]">
            <Serif>Toda tu operación de obra,</Serif>
            <br />
            <Serif className="italic">
              <span style={{ color: '#b15a3c' }}>en una sola plataforma.</span>
            </Serif>
          </h1>

          <p className="text-lg sm:text-xl text-[#5b554c] max-w-2xl mx-auto mb-10 leading-relaxed">
            Asistencia GPS, nómina LFT, finanzas por obra con costo real,
            compras con factura adjunta, inventario, tesorería y un asistente de
            IA. Seguridad de nivel empresarial.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-16">
            <button
              onClick={goLogin}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#1f1d1a] hover:bg-[#2c2925] text-[#f5f1ea] rounded-xl font-semibold transition-colors shadow-md"
            >
              Comenzar ahora
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="#modulos"
              className="inline-flex items-center justify-center px-8 py-3.5 bg-white hover:bg-[#faf7f1] text-[#1f1d1a] border border-[#e7dfd1] rounded-xl font-semibold transition-colors"
            >
              Ver módulos
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-10 max-w-2xl mx-auto pt-10 border-t border-[#e7dfd1]">
            {[
              { v: '14+', l: 'Módulos integrados' },
              { v: '4', l: 'Niveles de acceso' },
              { v: 'PWA', l: 'Funciona offline' },
            ].map(s => (
              <div key={s.l}>
                <div
                  className="text-3xl sm:text-4xl text-[#1f1d1a]"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 600,
                  }}
                >
                  {s.v}
                </div>
                <div className="text-xs sm:text-sm text-[#7d7468] mt-2 tracking-wide">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Módulos ─────────────────────────────────────────────────────── */}
      <section id="modulos" className="py-24" style={{ background: '#ebe4d8' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <Eyebrow>MÓDULOS</Eyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1f1d1a] mb-4">
              <Serif>Todo lo que tu obra necesita,</Serif>{' '}
              <Serif className="italic">
                <span style={{ color: '#b15a3c' }}>integrado.</span>
              </Serif>
            </h2>
            <p className="text-[#5b554c] max-w-2xl mx-auto">
              Una plataforma unificada — operación, finanzas, compras,
              inventario, RH y BI.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {MODULES.map(({ Icon: Ico, title, desc }) => (
              <div
                key={title}
                className="group bg-white border border-[#e7dfd1] rounded-xl p-5 hover:border-[#c9943a] hover:shadow-[0_8px_20px_-12px_rgba(31,29,26,0.25)] transition-all"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-colors"
                  style={{ background: '#1f1d1a' }}
                >
                  <Ico className="w-5 h-5" style={{ color: '#f5f1ea' }} />
                </div>
                <h3
                  className="font-semibold text-[#1f1d1a] mb-1"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {title}
                </h3>
                <p className="text-sm text-[#5b554c] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features destacados ─────────────────────────────────────────── */}
      <section className="py-24" style={{ background: '#f5f1ea' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-24">
          {FEATURES.map(
            ({ eyebrow, title, desc, bullets, mockTitle, mockRows }, i) => {
              const reverse = i % 2 === 1;
              return (
                <div
                  key={title}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center`}
                >
                  <div className={reverse ? 'lg:order-2' : ''}>
                    <Eyebrow>{eyebrow}</Eyebrow>
                    <h3 className="text-3xl sm:text-4xl text-[#1f1d1a] mb-5 leading-tight">
                      <Serif>{title}</Serif>
                    </h3>
                    <p className="text-[#5b554c] text-lg leading-relaxed mb-7">
                      {desc}
                    </p>
                    <ul className="space-y-3">
                      {bullets.map(b => (
                        <li key={b} className="flex items-start gap-3">
                          <CheckCircle2
                            className="w-5 h-5 flex-shrink-0 mt-0.5"
                            style={{ color: '#7a8b5e' }}
                          />
                          <span className="text-[#3d3933]">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={reverse ? 'lg:order-1' : ''}>
                    <MockCard title={mockTitle} rows={mockRows} />
                  </div>
                </div>
              );
            }
          )}
        </div>
      </section>

      {/* ── Seguridad ───────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: '#ebe4d8' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <Eyebrow>SEGURIDAD</Eyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1f1d1a] mb-4">
              <Serif>De nivel</Serif>{' '}
              <Serif className="italic">
                <span style={{ color: '#b15a3c' }}>empresarial.</span>
              </Serif>
            </h2>
            <p className="text-[#5b554c] max-w-2xl mx-auto">
              Tus datos protegidos con las mejores prácticas, desde la base de
              datos hasta el cliente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                Icon: Lock,
                title: 'RBAC',
                desc: 'Control de acceso por rol: SuperAdmin, Admin, Supervisor y Usuario.',
              },
              {
                Icon: Database,
                title: 'RLS',
                desc: 'Row Level Security — cada usuario solo ve sus obras asignadas.',
              },
              {
                Icon: Eye,
                title: 'Auditoría',
                desc: 'Bitácora completa con timestamp, usuario, IP y user-agent.',
              },
              {
                Icon: Shield,
                title: 'Encriptación',
                desc: 'PostgreSQL más Supabase Auth con MFA opcional y URLs firmadas.',
              },
            ].map(({ Icon: Ico, title, desc }) => (
              <div
                key={title}
                className="bg-white p-6 rounded-xl border border-[#e7dfd1]"
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                  style={{ background: '#1f1d1a' }}
                >
                  <Ico className="w-5 h-5" style={{ color: '#f5f1ea' }} />
                </div>
                <h3
                  className="font-semibold text-[#1f1d1a] mb-2"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {title}
                </h3>
                <p className="text-sm text-[#5b554c] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Roles ───────────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: '#f5f1ea' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <Eyebrow>ROLES</Eyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1f1d1a] mb-4">
              <Serif>Un perfil para</Serif>{' '}
              <Serif className="italic">
                <span style={{ color: '#b15a3c' }}>cada necesidad.</span>
              </Serif>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ROLES.map(({ Icon: Ico, title, desc, badge }) => (
              <div
                key={title}
                className="bg-white rounded-xl p-6 border border-[#e7dfd1] hover:border-[#c9943a] hover:shadow-[0_8px_20px_-12px_rgba(31,29,26,0.25)] transition-all"
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                  style={{ background: '#faf0e0' }}
                >
                  <Ico className="w-5 h-5" style={{ color: '#b15a3c' }} />
                </div>
                <h3
                  className="font-semibold text-[#1f1d1a] mb-1"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {title}
                </h3>
                <p className="text-sm text-[#5b554c] mb-4 leading-relaxed">
                  {desc}
                </p>
                <div
                  className="text-[10px] font-semibold tracking-widest inline-flex px-2 py-1 rounded-full"
                  style={{ background: '#f3e6d4', color: '#8a5a1a' }}
                >
                  {badge.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: '#ebe4d8' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Eyebrow>PREGUNTAS</Eyebrow>
            <h2 className="text-3xl sm:text-4xl text-[#1f1d1a] mb-3">
              <Serif>Lo esencial,</Serif>{' '}
              <Serif className="italic">
                <span style={{ color: '#b15a3c' }}>respondido.</span>
              </Serif>
            </h2>
          </div>
          <div className="bg-white rounded-xl border border-[#e7dfd1] px-5 sm:px-7">
            {FAQ_DATA.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────────────────── */}
      <section
        className="py-24 relative overflow-hidden"
        style={{ background: '#1f1d1a' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(201,148,58,0.18) 0%, transparent 60%)',
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl mb-5 leading-tight text-[#f5f1ea]">
            <Serif>Centraliza tu obra.</Serif>
            <br />
            <Serif className="italic">
              <span style={{ color: '#e2b870' }}>Decide con datos reales.</span>
            </Serif>
          </h2>
          <p className="text-[#c9bfae] text-lg mb-9 max-w-xl mx-auto">
            Empieza hoy con tus equipos, tus obras y tus números, sin
            migraciones complejas.
          </p>
          <button
            onClick={goLogin}
            className="inline-flex items-center justify-center gap-2 px-9 py-3.5 bg-[#f5f1ea] hover:bg-white text-[#1f1d1a] rounded-xl font-semibold transition-colors shadow-lg"
          >
            Comenzar ahora
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer
        className="py-10"
        style={{ background: '#141311', color: '#a89886' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-sm text-center md:text-left">
            <p
              className="text-[#f5f1ea] mb-1"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 600,
              }}
            >
              {companyName}
            </p>
            <p>
              Diseñado y desarrollado por Alejandro Trejo y Nova Axis
              Consulting.
            </p>
          </div>
          <a
            href="https://novaaxisconsulting.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-opacity hover:opacity-80"
          >
            <img
              src="/assets/images/nova-axis-logo.png"
              alt="Nova Axis Consulting"
              className="h-9 w-auto opacity-90"
            />
          </a>
        </div>
      </footer>
    </div>
  );
}
